import { Fragment, useEffect, useMemo, useState } from 'react';
import { api, type PermissionDef, type RoleDef, type ProjectMemberRow, type ProjectListItem } from '../api';
import { Page, Card, Select } from '../ui';

/** Página de administração de RBAC: perfis × permissões (editável) + usuários × perfil por projeto. */
export function AdminRoles() {
  const [permissions, setPermissions] = useState<PermissionDef[]>([]);
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [matrix, setMatrix] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.listRoles()
      .then(({ permissions, roles, rolePermissions }) => {
        setPermissions(permissions);
        setRoles(roles);
        const m: Record<string, Set<string>> = {};
        for (const r of roles) m[r.key] = new Set(rolePermissions[r.key] ?? []);
        setMatrix(m);
      })
      .catch(() => setErr('Falha ao carregar perfis (precisa ser admin).'))
      .finally(() => setLoading(false));
  }, []);

  const areas = useMemo(() => {
    const map: Record<string, PermissionDef[]> = {};
    for (const p of permissions) (map[p.area] ??= []).push(p);
    return map;
  }, [permissions]);

  async function toggle(roleKey: string, perm: string) {
    const cur = new Set(matrix[roleKey] ?? []);
    if (cur.has(perm)) cur.delete(perm); else cur.add(perm);
    setMatrix((m) => ({ ...m, [roleKey]: cur }));
    try { await api.setRolePermissions(roleKey, [...cur]); setMsg('Salvo.'); setErr(null); }
    catch { setErr('Falha ao salvar'); }
  }

  return (
    <Page title="Perfis & Permissões">
      {msg && <div style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 8 }}>{msg}</div>}
      {err && <div className="chat-error">{err}</div>}
      {loading ? (
        <div className="center">Carregando…</div>
      ) : (
        <Card style={{ overflowX: 'auto' }}>
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 0 }}>
            Cada coluna é um perfil (por projeto). Marque/desmarque pra ajustar o que cada um pode. <strong>admin</strong> é global e pode tudo.
          </p>
          <table className="rbac-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Permissão</th>
                {roles.map((r) => <th key={r.key} title={r.description ?? ''}>{r.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {Object.entries(areas).map(([area, perms]) => (
                <Fragment key={area}>
                  <tr className="rbac-area"><td colSpan={roles.length + 1}>{area}</td></tr>
                  {perms.map((p) => (
                    <tr key={p.key}>
                      <td style={{ textAlign: 'left' }} title={p.key}>{p.label}</td>
                      {roles.map((r) => (
                        <td key={r.key} style={{ textAlign: 'center' }}>
                          <input type="checkbox" checked={matrix[r.key]?.has(p.key) ?? false} onChange={() => toggle(r.key, p.key)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <MembersSection roles={roles} />
    </Page>
  );
}

function MembersSection({ roles }: { roles: RoleDef[] }) {
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [slug, setSlug] = useState('');
  const [members, setMembers] = useState<ProjectMemberRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listProjects().then(({ projects }) => { setProjects(projects); if (projects[0]) setSlug(projects[0].slug); }).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (slug) api.listMembers(slug).then(({ members }) => setMembers(members)).catch(() => setMembers([]));
  }, [slug]);

  async function changeRole(userId: string, roleKey: string) {
    try {
      await api.setMemberRole(slug, userId, roleKey);
      setMembers((ms) => ms.map((m) => (m.userId === userId ? { ...m, roleKey } : m)));
      setErr(null);
    } catch { setErr('Falha ao mudar o perfil'); }
  }

  return (
    <Card style={{ marginTop: 16, overflowX: 'auto' }}>
      <div className="card-title-row"><strong>Usuários × perfil (por projeto)</strong></div>
      {err && <div className="chat-error">{err}</div>}
      <Select value={slug} onChange={(e) => setSlug(e.target.value)} style={{ maxWidth: 280, margin: '8px 0 12px' }}>
        {projects.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
      </Select>
      <table className="rbac-table">
        <thead><tr><th style={{ textAlign: 'left' }}>Usuário</th><th>Global</th><th>Perfil no projeto</th></tr></thead>
        <tbody>
          {members.map((m) => (
            <tr key={m.userId}>
              <td style={{ textAlign: 'left' }}>{m.displayName} <span style={{ color: 'var(--muted)' }}>({m.email})</span></td>
              <td style={{ textAlign: 'center' }}>{m.globalRole === 'admin' ? '👑 admin' : '—'}</td>
              <td style={{ textAlign: 'center' }}>
                {m.globalRole === 'admin' ? (
                  <em style={{ color: 'var(--muted)' }}>tudo (admin global)</em>
                ) : (
                  <Select value={m.roleKey ?? ''} onChange={(e) => changeRole(m.userId, e.target.value)} style={{ maxWidth: 200 }}>
                    {!m.roleKey && <option value="">(sem perfil)</option>}
                    {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </Select>
                )}
              </td>
            </tr>
          ))}
          {members.length === 0 && <tr><td colSpan={3} style={{ color: 'var(--muted)' }}>nenhum membro</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
